import * as Sentry from "@sentry/browser";
import Sessioncore from "@storyboard-fm/sessioncore";
import Bowser from "bowser";
import cuid from "cuid";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTus } from "use-tus";

import { ampli } from "@/config";
import useLocationChange from "@/hooks/useLocationChange";
import { createLogger } from "@/logger";
import { AppContext } from "@/models/AppStateProvider";
import { AudioAppContext } from "@/models/AudioAppContextProvider";
import { publishToWorkspaceFeed } from "@/models/actions/clientActions";
import { sendContentEvent } from "@/models/contentEvents";
import { DataContext } from "@/models/DataProvider";
import { recordingUploadMetadata } from "@/models/recordingUpload";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { recordEvent } from "@/services/telemetry";
import { AUDIO_FILTERS, getBandpassData } from "@/utils/audio";
import {
  type AudioNodeInterface,
  cancelWakeLock,
  clearIndexDB,
  fetchAudioMetaData,
  fetchUploadConfig,
  forceFileDownload,
  getPaddedTime,
  isWavPcmDurationMismatch,
  monitorAudioLevels,
  readWavPcmFormat,
  requestWakeLock,
  setupAudioNode,
} from "@/utils/recording";

const log = createLogger("upload");

const sessionCorePrefix = "sessioncore-";
const maxRecordingLength = Number(import.meta.env.VITE_SESSION_RECORDING_LENGTH ?? 900);
const intervalLength = Number(import.meta.env.VITE_SESSION_RECORDING_INTERVAL ?? 90);

type SessionCore = {
  id: string;
  ctx: AudioContext;
  source: MediaStreamAudioSourceNode;
  setup: () => void;
  start: () => void;
  stop: () => void;
  wav: () => Blob;
  blob: () => Blob;
  on: (event: string, listener: (...args: unknown[]) => void) => void;
};

type RecordingTime = { milliseconds: number; seconds: number; time: string };

const INITIAL_TIME: RecordingTime = { milliseconds: 0, seconds: 0, time: "00:00" };

/** The ring's color through each interval of a recording, green going red as it nears the limit. */
const INTERVAL_COLORS = [
  "#00ff04",
  "#00ff04",
  "#84ff00",
  "#c8ff00",
  "#fffb00",
  "#FFC400FF",
  "#ff9900",
  "#ff7300",
  "#ff4400",
  "#ff1e00",
  "#ff0073",
];

/** What a composer renders from a recording in progress, and the three things it can do to it. */
export type VoiceRecorder = {
  recording: boolean;
  /** Encoding and checking the audio after Stop, before the upload starts. */
  processing: boolean;
  /** The upload's progress, 0 until it has started. */
  uploadProgress: number;
  /** The share of the current interval elapsed, 0 while not recording; the classic button reads it as recording. */
  progress: number;
  audioLevel: number;
  showAudioLevels: boolean;
  /** The elapsed time as `mm:ss`. */
  time: string;
  /** The ring color for how far into the recording limit it is. */
  color: string;
  /** The record button waits: a stop in flight, processing, or an upload running. */
  disabled: boolean;
  /** Processing or uploading: the recording has left the microphone and is on its way. */
  uploading: boolean;
  error: Error | null;
  clearError: () => void;
  start: () => Promise<void>;
  /** Stops the recording and sends it. */
  stop: () => Promise<boolean | undefined>;
  cancel: () => Promise<void>;
};

/**
 * Records a voice message with Sessioncore and sends it to a channel the way the driver list always has.
 *
 * Stop publishes the item to `feedId` with `publishToWorkspaceFeed`, then uploads the WAV over TUS with `inputType:
 * "Recording"`, checking first that the file matches the time recorded so a truncated recording is never sent. A
 * recording cancels when the URL changes or when `feedId` does, so it never lands in a channel the dispatcher left.
 * Lifted out of the classic `SessionRecorderFooter` so the Dispatch composer records the same way.
 *
 * @param workspaceId - The workspace the recording is published in.
 * @param feedId - The channel it goes to; a change cancels a recording in progress.
 * @param isSilent - Read when the recording is published: whether the channel is silent, which the item carries.
 * @param onPublished - Called once the item is published, before the upload finishes.
 * @param onStopped - Called when a recording stops, sent or cancelled, for the caller to take the focus back.
 * @param onFinished - Called at the end of a stop, whether the send went out or failed.
 */
export function useVoiceRecorder({
  workspaceId,
  feedId,
  isSilent,
  onPublished,
  onStopped,
  onFinished,
}: {
  workspaceId: string;
  feedId: string;
  isSilent: () => boolean;
  onPublished?: () => void;
  onStopped?: () => void;
  onFinished?: () => void;
}): VoiceRecorder {
  const { config, flags } = useContext(AppContext);
  const { preferredLanguage } = useContext(DataContext);
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const { myAccount } = useContext(MyAccountContext);
  const { playerPlaying, pauseItem } = useContext(AudioAppContext);
  const forceDownload: boolean = flags.recordingDebug ?? false;

  const { upload, setUpload } = useTus();
  const { platform, browser } = Bowser.parse(window.navigator.userAgent);
  const isFirefox = browser?.name?.toLowerCase()?.includes("firefox");
  const isMobile = platform.type === "mobile";

  const requestRef = useRef<number | undefined>(undefined);
  const sessionCore = useRef<SessionCore>({} as SessionCore);
  const startTime = useRef<Date>(new Date());
  const queuePlayingRef = useRef<boolean>(playerPlaying);
  // Set inside start() so the stop flow can check whether the active sessioncore emitted any append/write errors
  // during recording.
  const sessionWriteFailedRef = useRef<() => boolean>(() => false);

  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [audioNode, setAudioNode] = useState<AudioNodeInterface | null>(null);
  const [color, setColor] = useState<string>("");
  const [counter, setCounter] = useState<number>(0);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [recording, setRecording] = useState<boolean>(false);
  const [sessionName, setSessionName] = useState<string>(`${sessionCorePrefix}${Date.now()}`);
  const [timeObject, setTimeObject] = useState<RecordingTime>(INITIAL_TIME);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [wakeLock, setWakeLock] = useState(null);
  const [contentId, setContentId] = useState<string | null>(null);

  const disabledState = useMemo(() => {
    if (disabled) return true;
    if (error) return false;
    return processing || uploadProgress > 0;
  }, [processing, uploadProgress, error, disabled]);

  const markUploadAsComplete = () => {
    setProcessing(() => false);
    setContentId(() => null);
    setUploadProgress(() => 0);
    setCounter(() => 0);
    setDisabled(() => false);
    clearIndexDB(sessionName, null).then((data) => data);
  };

  const setupUploadData = async (file: Blob, duration: number) => {
    const { stage, tusUrl } = fetchUploadConfig(config);
    const uploadContentId = contentId || cuid();

    const item = await publishToWorkspaceFeed({
      workspaceId,
      feedId,
      contentId: uploadContentId,
      isSilent: isSilent(),
    });
    onPublished?.();

    const itemId = item?.id;
    sendContentEvent({
      contentId: uploadContentId,
      step: "client_uploading",
      status: "started",
      feedId,
      workspaceId: currentWorkspaceId,
    }).then(() => {});
    const uploadStartTime = Date.now();

    const metadata = recordingUploadMetadata({
      contentId: uploadContentId,
      fileType: file.type,
      stage,
      itemId,
      feedId,
      accountId: myAccount?.id,
      durationSeconds: duration,
      preferredLanguage,
      audio: fetchAudioMetaData(sessionCore.current.ctx),
    });

    setUpload(file, {
      storeFingerprintForResuming: true,
      endpoint: tusUrl,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      metadata,
      onChunkComplete: (chunkSize, bytesAccepted, bytesTotal) => {
        log.debug("onChunkComplete", { chunkSize, bytesAccepted, bytesTotal });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        setUploadProgress(() => 1);
        if (bytesUploaded > 0) {
          const percentage = Math.floor((bytesUploaded / bytesTotal) * 100);
          setUploadProgress(() => percentage);
        }
      },
      onError: async (uploadError) => {
        console.error("There wan an error:", uploadError);
        ampli.uploadFailed({ feedId });
        setUploadProgress(() => 0);
        setProcessing(() => false);
        setDisabled(() => false);
        setError(() => uploadError);
        // Retries are exhausted and the recording upload is lost without user action —
        // unrecoverable, so page Sentry with the recording context.
        Sentry.captureException(uploadError, {
          tags: { feature: "voice-recording", step: "upload-failed" },
          extra: { feedId, workspaceId: currentWorkspaceId, contentId: uploadContentId },
        });
        sendContentEvent({
          workspaceId: currentWorkspaceId,
          contentId: uploadContentId,
          step: "client_uploading",
          status: "failed",
          feedId,
          context: {
            milliseconds: Date.now() - uploadStartTime,
          },
          error: uploadError,
        }).then(() => {});
      },
      onSuccess: async () => {
        log.debug("File Upload Complete");
        sendContentEvent({
          workspaceId: currentWorkspaceId,
          contentId: uploadContentId,
          step: "client_uploading",
          status: "finished",
          feedId,
          context: {
            milliseconds: Date.now() - uploadStartTime,
          },
        }).then(() => {});
        setError(() => null);
        markUploadAsComplete();
        ampli.uploadRecord();
      },
      onShouldRetry: (err, retryAttempt, options) => {
        log.error("Upload retry", err, retryAttempt, options);
        // Retried automatically — a reliability trend, not a page. onError covers the case
        // where retries run out.
        recordEvent("recording.upload_retry", {
          recording: { feedId, retryAttempt, error: err instanceof Error ? err.message : String(err) },
        });
        return true;
      },
    });
  };

  /** Builds the elapsed time on every animation frame. */
  const buildTimeObject = () => {
    requestRef.current = requestAnimationFrame(buildTimeObject);
    const now = Date.now();
    const then = startTime.current.getTime();
    const timeFloorAbs = Math.floor(Math.abs(then - now) / 1000);
    setTimeObject(() => ({
      milliseconds: Math.floor(Math.abs(then - now)),
      seconds: timeFloorAbs,
      time: getPaddedTime(timeFloorAbs),
    }));
  };

  const startTimer = () => {
    startTime.current = new Date();
    cancelAnimationFrame(requestRef.current);
    requestRef.current = requestAnimationFrame(buildTimeObject);
  };

  const stopTimer = () => {
    setTimeObject(() => INITIAL_TIME);
    cancelAnimationFrame(requestRef.current);
  };

  /** Sets up Sessioncore and the level meter, then starts the timer. */
  const start = async () => {
    setError(() => null);

    const localSessionName = cuid();
    setContentId(() => localSessionName);
    setSessionName(() => localSessionName);
    sendContentEvent({
      workspaceId: currentWorkspaceId,
      contentId: localSessionName,
      step: "client_recording",
      status: "started",
      feedId,
    }).then(() => {});
    const session = new Sessioncore(localSessionName);
    // Sessioncore emits 'error' when hypercore.append() fails mid-recording
    // (e.g. IndexedDB quota exhaustion or a transient write failure). The
    // library silently drops the failed batch, so without a listener long
    // recordings can end up truncated in the field with no signal (WEB-1112).
    // Surface the first failure to Sentry/Amplitude and to the user so we can
    // diagnose and the recording isn't trusted blindly.
    let sessionWriteFailed = false;
    session.on("error", (err) => {
      const reportedError = err instanceof Error ? err : new Error(String(err));
      log.error("Sessioncore emitted error during recording", reportedError);
      if (!sessionWriteFailed) {
        sessionWriteFailed = true;
        Sentry.captureException(reportedError, {
          extra: { contentId: localSessionName },
          tags: { feature: "voice-recording", step: "sessioncore-error" },
        });
        ampli.localProcessingError({
          duration: 0,
          expectedDuration: 0,
          message: `sessioncore error during recording: ${reportedError.message}`,
        });
      }
    });
    sessionWriteFailedRef.current = () => sessionWriteFailed;
    const f1 = AUDIO_FILTERS.STANDARD.HIGHPASS;
    const f2 = AUDIO_FILTERS.STANDARD.LOWPASS;
    const { centerFreq, q } = getBandpassData(f1, f2);
    await session.setup(null, { bandpass: [centerFreq, q] });
    await session.start();
    const localAudioNode = await setupAudioNode();
    setAudioNode(() => localAudioNode ?? null);
    const localWakeLock = await requestWakeLock();

    // Conditionally wait for firefox to load sessioncore, since it's not instant; 2 seconds seems to be a long enough
    // time to wait for it to load.
    if (isFirefox) {
      await new Promise((resolve) => setTimeout(resolve, 2200));
    }

    // set the previous playback state before we pause the recording
    queuePlayingRef.current = playerPlaying;
    if (playerPlaying) {
      pauseItem();
    }
    setWakeLock(() => localWakeLock);
    setRecording(() => true);
    startTimer();
    ampli.startRecord({ feedId });
    sessionCore.current = session;
  };

  const compareTimerDurationWithSessionCoreDuration = (duration: number, sessionCoreDuration: number) => {
    // how long the timer says the audio is
    const loggedDuration: number = Math.ceil(duration);
    // how long session core says the audio is
    const sessionDuration: number = Math.floor(sessionCoreDuration);
    // Allow ±10% (with a 10s floor) of the timer-measured duration to account for
    // small encoder / scheduling skew. Anything outside that window indicates the
    // produced audio was truncated or extended relative to what the user recorded.
    const allowableRange: number = Math.max(loggedDuration / 10, 10);
    const low: number = loggedDuration - allowableRange;
    const high: number = loggedDuration + allowableRange;
    const durationMisMatch: boolean = sessionDuration < low || sessionDuration > high;

    if (durationMisMatch) {
      ampli.localProcessingError({
        duration: loggedDuration,
        expectedDuration: sessionDuration,
        message: "sessioncore audio duration does not match timer duration",
      });
    }
  };

  /** Stops the recording, checks the audio, and sends it. */
  const stop = async () => {
    const duration = timeObject.milliseconds / 1000;
    // prevent double clicks
    if (disabled) {
      return false;
    }
    sendContentEvent({
      contentId: contentId,
      step: "client_recording",
      status: "finished",
      feedId,
      workspaceId: currentWorkspaceId,
      context: {
        milliseconds: timeObject.milliseconds,
      },
    }).then(() => {});
    setDisabled(() => true);
    setProcessing(() => true);
    stopTimer();
    const startProcessingTime = Date.now();
    sendContentEvent({
      contentId: contentId,
      step: "client_processing",
      status: "started",
      feedId,
      workspaceId: currentWorkspaceId,
    }).then(() => {});
    // wait 1/2 extra second to ensure all recording is complete.
    await new Promise((resolve) => setTimeout(resolve, 500));
    await sessionCore.current.stop();
    setAudioLevel(() => 0);
    setCounter(() => 0);
    setRecording(() => false);
    onStopped?.();

    let file: Blob | null = null;
    try {
      file = await sessionCore.current.wav();
    } catch (e) {
      log.error("Failed to encode WAV from sessioncore", e);
      Sentry.captureException(e, {
        extra: { contentId, durationSeconds: duration },
        tags: { feature: "voice-recording", step: "encode-wav" },
      });
    }

    if (!file) {
      ampli.audioProcessingTimeout();
    }

    let processingDuration = 0;
    try {
      compareTimerDurationWithSessionCoreDuration(duration, sessionCore.current.ctx?.currentTime ?? duration);

      if (file && forceDownload) {
        forceFileDownload(file, `${sessionName}.wav`, "audio/x-wav");
      }

      processingDuration = Date.now() - startProcessingTime;
      const writeFailedDuringRecording = sessionWriteFailedRef.current();
      if (file) {
        const wavFormat = await readWavPcmFormat(file);
        let expectedBytes: number | undefined;
        let sizeMismatch = false;
        if (wavFormat) {
          const wavDurationCheck = isWavPcmDurationMismatch(duration, wavFormat);
          sizeMismatch = wavDurationCheck.mismatch;
          expectedBytes = 44 + wavDurationCheck.expectedPcmBytes;
          if (sizeMismatch) {
            ampli.localProcessingError({
              duration: Math.ceil(duration),
              expectedDuration: Math.round(wavDurationCheck.pcmDurationSeconds),
              message: "wav file size does not match recorded duration",
            });
          }
        }
        // If sessioncore had a write error or the wav size doesn't match the
        // recorded duration, bail out instead of uploading a truncated file.
        if (writeFailedDuringRecording || sizeMismatch) {
          setProcessing(() => false);
          setDisabled(() => false);
          setError(
            () => new Error("Recording may be incomplete due to a browser storage error. Please try sending it again."),
          );
          sendContentEvent({
            contentId: contentId,
            step: "client_processing",
            status: "failed",
            feedId,
            workspaceId: currentWorkspaceId,
            context: {
              milliseconds: processingDuration,
              fileBytes: file.size,
              expectedBytes,
              writeFailedDuringRecording,
              sizeMismatch,
            },
          }).then(() => {});
        } else {
          sendContentEvent({
            contentId: contentId,
            step: "client_processing",
            status: "finished",
            feedId,
            workspaceId: currentWorkspaceId,
            context: {
              milliseconds: processingDuration,
              fileBytes: file.size,
              expectedBytes,
            },
          }).then(() => {});
          await setupUploadData(file, duration);
        }
      } else {
        // No file produced — surface the failure to the user instead of leaving
        // them stuck on a spinning hourglass forever (see WEB-1112).
        setProcessing(() => false);
        setDisabled(() => false);
        setError(() => new Error("Failed to process recording. Please try again."));
        sendContentEvent({
          contentId: contentId,
          step: "client_processing",
          status: "failed",
          feedId,
          workspaceId: currentWorkspaceId,
          context: {
            milliseconds: processingDuration,
          },
        }).then(() => {});
      }
    } catch (e) {
      log.error("Failed during recording post-processing", e);
      Sentry.captureException(e, {
        extra: { contentId, durationSeconds: duration },
        tags: { feature: "voice-recording", step: "post-encode" },
      });
      processingDuration = Date.now() - startProcessingTime;
      setProcessing(() => false);
      setDisabled(() => false);
      setError(() => new Error("Failed to process recording. Please try again."));
      sendContentEvent({
        contentId: contentId,
        step: "client_processing",
        status: "failed",
        feedId,
        workspaceId: currentWorkspaceId,
        context: {
          milliseconds: processingDuration,
        },
      }).then(() => {});
    } finally {
      // close/stop any tracks for force stop recording
      audioNode?.mediaStream?.getTracks().forEach((track) => track.stop());
      await cancelWakeLock(wakeLock);
    }
    onFinished?.();
  };

  /** Cancels the recording and clears it without sending. */
  const cancel = async () => {
    sendContentEvent({
      contentId: contentId,
      step: "client_recording",
      status: "canceled",
      feedId,
      workspaceId: currentWorkspaceId,
      context: {
        milliseconds: timeObject.milliseconds,
      },
    }).then(() => {});
    stopTimer();
    setContentId(() => null);
    setAudioLevel(() => 0);
    setCounter(() => 0);
    setProcessing(() => false);
    // small delay for animations
    setTimeout(() => {
      setRecording(() => false);
      onStopped?.();
    }, 300);
    await sessionCore.current.stop();
    audioNode?.mediaStream.getTracks().forEach((track) => track.stop());
    await cancelWakeLock(wakeLock);
  };

  /** Sets the interval the recording is in, which the ring's color and progress follow. */
  const setCurrentCounter = useCallback(
    (seconds: number) => setCounter(() => Math.floor(seconds / intervalLength)),
    [],
  );

  /** A smooth progress through the current interval. */
  const calculateRadialProgressInMilliSeconds = useCallback(
    (milliseconds: number) => {
      const seconds = milliseconds / 1000;
      const multiplier = counter * 100;
      const radialProgressPercentage = Number((Math.ceil(seconds * 100) / intervalLength - multiplier).toFixed(2));
      if (radialProgressPercentage > 100) {
        setProgress(() => 0);
      } else {
        setProgress(() => radialProgressPercentage);
      }
    },
    [counter],
  );

  // Auto-stops the recording once it reaches the maximum length.
  const stopRef = useRef(stop);
  stopRef.current = stop;
  const autoStopRecording = useCallback(
    async (milliseconds: number) => {
      const maxMilliseconds = maxRecordingLength * 1000;
      if (milliseconds >= maxMilliseconds && !disabled) {
        await stopRef.current();
      }
    },
    [disabled],
  );

  // Uploads the file once it is ready.
  useEffect(() => {
    if (upload) {
      setProcessing(() => false);
      upload.start();
    }
  }, [upload]);

  useEffect(() => {
    const intervalColor = INTERVAL_COLORS[counter];
    if (intervalColor) setColor(() => intervalColor);
  }, [counter]);

  useEffect(() => {
    autoStopRecording(timeObject.milliseconds);
    calculateRadialProgressInMilliSeconds(timeObject.milliseconds);
    setAudioLevel(() => monitorAudioLevels(audioNode, isMobile));
  }, [timeObject.milliseconds, autoStopRecording, calculateRadialProgressInMilliSeconds, audioNode, isMobile]);

  useEffect(() => {
    setCurrentCounter(timeObject.seconds || 0);
  }, [timeObject.seconds, setCurrentCounter]);

  // On mount, clear what earlier sessions left in IndexedDB.
  useEffect(() => {
    clearIndexDB(null, sessionCorePrefix);
  }, []);

  // A recording never outlives the page it was started on, nor the channel it was meant for.
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;
  const recordingRef = useRef(recording);
  recordingRef.current = recording;
  useLocationChange(() => {
    if (recordingRef.current) cancelRef.current();
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: a change of channel is the trigger, not an input
  useEffect(() => {
    if (recordingRef.current) cancelRef.current();
  }, [feedId]);

  const showAudioLevels = !!(recording && audioLevel && !disabled && timeObject.milliseconds > 0);
  const clearError = useCallback(() => setError(null), []);

  return {
    recording,
    processing,
    uploadProgress,
    progress,
    audioLevel,
    showAudioLevels,
    time: timeObject.time,
    color,
    disabled: disabledState,
    uploading: processing || uploadProgress > 0,
    error,
    clearError,
    start,
    stop,
    cancel,
  };
}
